import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, X, Play } from 'lucide-react';
import { FaGithub } from 'react-icons/fa';
import { portfolioData } from '../data/data';

const ImageLoader = ({ src, alt, className }: { src: string, alt: string, className: string }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <>
      {!isLoaded && (
        <div className="absolute inset-0 bg-white/5 animate-pulse"></div>
      )}
      <img
        src={src}
        alt={alt}
        className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setIsLoaded(true)}
      />
    </>
  );
};

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [showAll, setShowAll] = useState(false);

  const displayedProjects = portfolioData.slice(0, 3);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="bg-svnate-darkNavy py-24 px-6 md:px-12 relative" id="portfolio">
      <div className="container mx-auto relative z-10 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-16 text-center"
        >
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-white/10 bg-[#0f1419] ">
            <span className="text-svnate-lightTeal font-semibold text-sm">Selected Work</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
            Our Portfolio
          </h2>
          <p className="text-lg md:text-xl text-foreground/70 max-w-2xl mx-auto">
            Real projects, real impact. See how we've helped businesses transform their digital presence and achieve their goals.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {displayedProjects.map((project, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              onClick={() => setSelectedProject(project)}
              className="bg-[#0f1419] border border-white/10 rounded-2xl group cursor-pointer hover:bg-white/10 hover:border-white/20 transition-all duration-300 flex flex-col h-full overflow-hidden hover:-translate-y-2 "
            >
              <div className={`relative h-64 bg-gradient-to-br ${project.gradient} overflow-hidden`}>
                {project.image ? (
                  <ImageLoader 
                    src={project.image} 
                    alt={`Project - ${project.title}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[#0f1419] group-hover:bg-white/10 transition-colors duration-300"></div>
                )}
                <div className="absolute top-4 left-4 bg-black/50  border border-white/10 px-3 py-1 font-medium text-xs uppercase tracking-widest text-white rounded-full">
                  {project.category}
                </div>
              </div>

              <div className="p-8 flex flex-col flex-1">
                <h3 className="text-2xl font-bold text-white group-hover:text-svnate-lightTeal mb-3 transition-colors duration-300">
                  {project.title}
                </h3>
                <p className="text-sm text-foreground/70 leading-relaxed mb-8 line-clamp-2">
                  {project.problem}
                </p>

                <div className="flex flex-wrap gap-2 mt-auto mb-6">
                  {project.techStack.slice(0, 3).map((tech: string, i: number) => (
                    <span
                      key={i}
                      className="px-2.5 py-1 text-xs font-medium bg-[#0f1419] text-white/80 rounded-md border border-white/10"
                    >
                      {tech}
                    </span>
                  ))}
                  {project.techStack.length > 3 && (
                    <span className="px-2.5 py-1 text-xs font-medium bg-[#0f1419] text-white/80 rounded-md border border-white/10">
                      +{project.techStack.length - 3}
                    </span>
                  )}
                </div>

                <div className="text-svnate-lightTeal font-medium text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                  View Case Study
                  <ExternalLink size={16} />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <AnimatePresence>
          {showAll && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8"
            >
              {portfolioData.slice(3).map((project, index) => (
                <motion.div
                  key={index + 3}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  onClick={() => setSelectedProject(project)}
                  className="bg-[#0f1419] border border-white/10 rounded-2xl group cursor-pointer hover:bg-white/10 hover:border-white/20 transition-all duration-300 flex flex-col h-full overflow-hidden hover:-translate-y-2 "
                >
                  <div className={`relative h-64 bg-gradient-to-br ${project.gradient} overflow-hidden`}>
                    {project.image ? (
                      <ImageLoader 
                        src={project.image} 
                        alt={`Project - ${project.title}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-[#0f1419] group-hover:bg-white/10 transition-colors duration-300"></div>
                    )}
                    <div className="absolute top-4 left-4 bg-black/50  border border-white/10 px-3 py-1 font-medium text-xs uppercase tracking-widest text-white rounded-full">
                      {project.category}
                    </div>
                  </div>

                  <div className="p-8 flex flex-col flex-1">
                    <h3 className="text-2xl font-bold text-white group-hover:text-svnate-lightTeal mb-3 transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="text-sm text-foreground/70 leading-relaxed mb-8 line-clamp-2">
                      {project.problem}
                    </p>

                    <div className="flex flex-wrap gap-2 mt-auto mb-6">
                      {project.techStack.slice(0, 3).map((tech: string, i: number) => (
                        <span
                          key={i}
                          className="px-2.5 py-1 text-xs font-medium bg-[#0f1419] text-white/80 rounded-md border border-white/10"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    <div className="text-svnate-lightTeal font-medium text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                      View Case Study
                      <ExternalLink size={16} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {portfolioData.length > 3 && (
          <div className="flex justify-center mt-12">
            <button
              onClick={() => setShowAll(!showAll)}
              className="px-8 py-3 bg-[#0f1419] text-white border border-white/10 rounded-full font-medium hover:bg-white/10 transition-colors duration-300 "
            >
              {showAll ? 'View Less' : 'Load More Projects'}
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-black/80 ">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-[#0f1419] border border-white/10 rounded-2xl max-w-5xl w-full relative flex flex-col max-h-[90vh] overflow-hidden shadow-2xl"
            >
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-black/50  rounded-full border border-white/10 text-white/70 hover:text-white flex items-center justify-center transition-colors z-10"
              >
                <X size={20} />
              </button>

              <div className={`p-8 md:p-12 bg-gradient-to-br ${selectedProject.gradient} relative border-b border-white/10`}>
                <span className="inline-block bg-black/50  border border-white/10 px-4 py-1.5 font-medium text-sm text-white rounded-full mb-6">
                  {selectedProject.category}
                </span>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                  {selectedProject.title}
                </h2>
                <p className="text-lg text-white/80 max-w-3xl">
                  {selectedProject.problem}
                </p>
              </div>

              <div className="p-8 md:p-12 overflow-y-auto flex-1 custom-scrollbar">
                <div className="flex flex-wrap gap-4 mb-12">
                  {selectedProject.liveLink && (
                    <a href={selectedProject.liveLink} target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-white text-black rounded-full font-medium hover:scale-105 transition-transform flex items-center gap-2">
                      <ExternalLink size={18} /> Website
                    </a>
                  )}
                  {selectedProject.playStoreLink && (
                    <a href={selectedProject.playStoreLink} target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-white/10 text-white border border-white/10 rounded-full font-medium hover:bg-white/20 transition-colors flex items-center gap-2">
                      <Play size={18} /> App
                    </a>
                  )}
                  {selectedProject.githubLink && (
                    <a href={selectedProject.githubLink} target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-white/10 text-white border border-white/10 rounded-full font-medium hover:bg-white/20 transition-colors flex items-center gap-2">
                      {React.createElement(FaGithub as any, { size: 18 })} Source
                    </a>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                  <div>
                    <div className="mb-12">
                      <h3 className="text-xl font-bold text-white mb-4">
                        The Solution
                      </h3>
                      <p className="text-foreground/70 leading-relaxed">
                        {selectedProject.solution}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-white mb-4">
                        Tech Stack
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedProject.techStack.map((tech: string, i: number) => (
                          <span key={i} className="px-3 py-1.5 text-sm font-medium bg-[#0f1419] border border-white/10 rounded-md text-white/80">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-4">
                        Results
                      </h3>
                      <ul className="space-y-4">
                        {selectedProject.results.map((result: string, i: number) => (
                          <li key={i} className="flex items-start gap-3 text-foreground/70">
                            <span className="text-svnate-lightTeal font-bold text-lg leading-none mt-0.5">•</span>
                            <span>{result}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;
